import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";

interface ImageZoomProps {
  src: string;
  alt: string;
  onClick?: () => void;
  className?: string;
}

const ZoomContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: zoom-in;
`;

const ZoomIcon = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.2rem;
  z-index: 10;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const PanZoomContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const MainImage = styled.img<{ zoom: number; panX: number; panY: number }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
  user-select: none;
  pointer-events: auto;
  transform: scale(${(props) => props.zoom})
    translate(${(props) => props.panX}px, ${(props) => props.panY}px);
  transform-origin: center center;
  cursor: ${(props) => (props.zoom > 1 ? "grab" : "zoom-in")};
`;

const ZoomLens = styled.div<{ visible: boolean; x: number; y: number }>`
  position: absolute;
  width: 150px;
  height: 150px;
  border: 2px solid #e97d7d;
  border-radius: 50%;
  background: rgba(233, 125, 125, 0.1);
  pointer-events: none;
  z-index: 10;
  left: ${(props) => props.x - 75}px;
  top: ${(props) => props.y - 75}px;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  backdrop-filter: blur(1px);

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    background: #e97d7d;
    border-radius: 50%;
    opacity: 0.7;
  }
`;

const ZoomWindow = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  width: 300px;
  height: 300px;
  border: 2px solid #e97d7d;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  left: ${(props) => props.x + 20}px;
  top: ${(props) => props.y - 150}px;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  overflow: hidden;
  pointer-events: none;
`;

const ZoomedImage = styled.img<{ offsetX: number; offsetY: number }>`
  position: absolute;
  width: 600px;
  height: 600px;
  object-fit: contain;
  left: ${(props) => -props.offsetX}px;
  top: ${(props) => -props.offsetY}px;
  transform: scale(2);
  transform-origin: top left;
`;

const ImageZoom: React.FC<ImageZoomProps> = ({
  src,
  alt,
  onClick,
  className,
}) => {
  const [isZooming, setIsZooming] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const { isDesktop } = useDeviceDetection();

  // Desktop: Custom zoom functionality
  const handleWheel = (e: React.WheelEvent) => {
    if (!isDesktop) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.max(1, Math.min(3, prev + delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isDesktop || zoom <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDesktop) {
      // Mobile/Tablet: Disable hover zoom - no magnifying glass
      return;
    } else if (isDragging && zoom > 1) {
      // Desktop pan
      setPanX(e.clientX - dragStart.x);
      setPanY(e.clientY - dragStart.y);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageClick = () => {
    if (isDesktop) {
      if (zoom === 1) {
        setZoom(2);
      } else {
        setZoom(1);
        setPanX(0);
        setPanY(0);
      }
    }
  };

  // Mobile/Tablet: Disable hover zoom (touch devices don't have hover)
  const handleMouseEnter = () => {
    // Only enable hover zoom for desktop with mouse
    if (isDesktop) {
      // Desktop hover functionality can be added here if needed
    }
  };

  const handleMouseLeave = () => {
    // Disable zoom on mouse leave
    if (!isDesktop) {
      setIsZooming(false);
    }
  };

  const handleClick = () => {
    if (isDesktop) {
      handleImageClick();
    } else {
      // For mobile/tablet, open lightbox
      if (onClick) {
        onClick();
      }
    }
  };

  return (
    <ZoomContainer
      ref={containerRef}
      className={className}
      onMouseEnter={isDesktop ? handleMouseEnter : undefined}
      onMouseLeave={isDesktop ? handleMouseLeave : undefined}
      onMouseMove={isDesktop ? handleMouseMove : undefined}
      onMouseDown={isDesktop ? handleMouseDown : undefined}
      onMouseUp={isDesktop ? handleMouseUp : undefined}
      onWheel={isDesktop ? handleWheel : undefined}
      onClick={handleClick}
      onTouchStart={(e) => {
        // Prevent magnifying glass on touch
        setIsZooming(false);
        // Allow click to open lightbox
      }}
      style={{
        cursor: isDesktop ? (zoom > 1 ? "grab" : "zoom-in") : "pointer",
        touchAction: isDesktop ? "auto" : "manipulation",
      }}
    >
      <PanZoomContainer>
        <MainImage
          ref={imageRef}
          src={src}
          alt={alt}
          draggable={false}
          zoom={zoom}
          panX={panX}
          panY={panY}
        />
      </PanZoomContainer>

      {/* Mobile/Tablet: Disabled - no magnifying glass on touch devices */}
    </ZoomContainer>
  );
};

export default ImageZoom;
